import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Icon } from "./icons";
import { STATUS_LABEL, coverUri } from "../lib/api";

export function Spinner({ pad = true }) {
  return (
    <div className={pad ? "center-pad" : ""}>
      <div className="spinner" />
    </div>
  );
}

export function Button({ children, variant = "primary", loading, disabled, icon, className = "", ...rest }) {
  return (
    <button
      className={`btn ${variant === "ghost" ? "ghost" : ""} ${variant === "danger" ? "danger" : ""} ${className}`}
      disabled={loading || disabled}
      {...rest}
    >
      {loading ? <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : (
        <>
          {icon ? <Icon name={icon} size={18} /> : null}
          {children}
        </>
      )}
    </button>
  );
}

export function Field({ label, as = "input", className = "", ...props }) {
  return (
    <label className="field">
      {label ? <span>{label}</span> : null}
      {as === "textarea" ? (
        <textarea className={`input ${className}`} {...props} />
      ) : (
        <input className={`input ${className}`} {...props} />
      )}
    </label>
  );
}

export function Chip({ label, active, onClick, ...rest }) {
  return (
    <button className={`chip ${active ? "active" : ""}`} onClick={onClick} {...rest}>
      {label}
    </button>
  );
}

export function ProgressBar({ value = 0 }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="track">
      <span style={{ width: `${v}%` }} />
    </div>
  );
}

export function StatusPill({ status, ...rest }) {
  const cls = status === "preparada" ? "done" : status === "em_estudo" ? "doing" : "todo";
  return (
    <span className={`pill ${cls}`} {...rest}>
      {(STATUS_LABEL[status] || "").toUpperCase()}
    </span>
  );
}

export function EmptyState({ icon = "musical-note-outline", text, ...rest }) {
  return (
    <div className="empty" {...rest}>
      <Icon name={icon} size={36} color="var(--muted)" />
      <span>{text}</span>
    </div>
  );
}

export function SongRow({ song, testid }) {
  return (
    <Link to={`/song/${song.id}`} className="song-row" data-testid={testid}>
      <img className="cover" src={coverUri(song.cover_url)} alt="" width={64} height={64} style={{ width: 64, height: 64, borderRadius: "var(--r-sm)" }} />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ fontFamily: "var(--f-medium)", fontWeight: 500, fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {song.title}
        </div>
        <div style={{ color: "var(--on-surface-3)", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{song.artist}</div>
        {song.project ? <div style={{ color: "var(--muted)", fontSize: 11 }}>{song.project}</div> : null}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <div style={{ flex: 1 }}>
            <ProgressBar value={song.progress} />
          </div>
          <span style={{ color: "var(--muted)", fontFamily: "var(--f-medium)", fontSize: 11, width: 34, textAlign: "right" }}>{song.progress ?? 0}%</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
        <StatusPill status={song.status} data-testid={testid ? `${testid}-status` : undefined} />
        <Icon name="chevron-forward" size={18} color="var(--muted)" />
      </div>
    </Link>
  );
}

export function Sheet({ title, children, onClose, testid, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div className="backdrop" onClick={onClose}>
      <div className={`sheet ${wide ? "sheet-wide" : ""}`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="handle" />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {title ? <div style={{ flex: 1, fontFamily: "var(--f-medium)", fontWeight: 500, fontSize: 20 }}>{title}</div> : <div style={{ flex: 1 }} />}
          <button className="icon-btn" onClick={onClose} data-testid="sheet-close" aria-label="Fechar" style={{ width: 36, height: 36 }}><Icon name="close" size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
